import { HTMLAttributes, forwardRef } from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from '@flowstate/utils'

const cardVariants = cva(
  "rounded-lg border bg-card/70 backdrop-blur-md text-card-foreground shadow-sm transition-colors duration-200",
  {
    variants: {
      variant: {
        default: "border-border/60 bg-card/70 backdrop-blur-md",
        glass: "border-border/40 bg-card/60 backdrop-blur-lg shadow-xl",
        outlined: "border-2 border-border/70 bg-card/65 backdrop-blur-md",
        elevated: "border-border/60 bg-card/80 backdrop-blur-md shadow-md",
        ghost: "border-transparent bg-transparent shadow-none backdrop-blur-none",
        destructive: "border-destructive/50 bg-destructive/10 backdrop-blur-md",
        warning: "border-yellow-200/50 bg-yellow-950/40 backdrop-blur-md dark:border-yellow-800/50",
        success: "border-green-200/50 bg-green-950/40 backdrop-blur-md dark:border-green-800/50",
        glow: "border-primary/30 bg-card/75 backdrop-blur-md card-glow",
      },
      padding: {
        none: "p-0",
        sm: "p-4",
        default: "p-6", 
        lg: "p-8",
      },
      interactive: {
        true: "cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "elevated",
        interactive: true,
        class: "hover:shadow-lg hover:-translate-y-1",
      },
      {
        variant: "ghost",
        interactive: true,
        class: "hover:bg-accent/50 hover:border-accent",
      },
      {
        variant: "destructive",
        interactive: true,
        class: "hover:bg-destructive/10 hover:border-destructive",
      },
      {
        variant: "glow",
        interactive: true,
        class: "hover:glow-primary-medium hover:-translate-y-1",
      },
    ],
    defaultVariants: {
      variant: "default",
      padding: "default",
      interactive: false,
    },
  }
)

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, padding, interactive, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="card"
      className={cn(cardVariants({ variant, padding, interactive, className }))}
      {...props}
    />
  )
)

const CardHeader = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="card-header"
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    data-slot="card-title"
    className={cn(
      "text-2xl font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    data-slot="card-description"
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} data-slot="card-content" className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="card-footer"
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
